import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  CalendarDays,
  ClipboardList,
  Flag,
  Gauge,
  MapPin,
  RefreshCw,
  Ruler,
  Smartphone,
} from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { dateTime, relativeTime } from '@/lib/format'

import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useMe } from '@/features/auth/api'
import { Link } from '@/lib/router'

import { type TripReviewFilters, type TripReviewItem, useTripReview } from './api'

type StatusPivot = 'completed' | 'active' | 'all'

const STATUS_FILTER: Record<StatusPivot, string | undefined> = {
  completed: 'COMPLETED,INTERRUPTED,AUTO_ENDED',
  active: 'ACTIVE',
  all: undefined,
}

/**
 * Manager Trip Review page (slice T4).
 *
 * The Approval Center handles trips before they happen. This page is the
 * post-flight view — every trip pinned to the viewer or run by an agent in
 * their Visit review (TRIP_REVIEW_VIEW) scope, with the T3
 * metrics (distance / efficiency) and the integrity-flag count. Filters:
 *   - Pivot tabs: Completed / Active / All — drives the status= query.
 *   - Per-agent UUID filter.
 *   - From/to date (inclusive).
 *   - Integrity-flagged only — chase mock-location incidents in one click.
 */
export function TripReviewPage() {
  const [pivot, setPivot] = useState<StatusPivot>('completed')
  const [agentFilter, setAgentFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [flaggedOnly, setFlaggedOnly] = useState(false)

  const filters: TripReviewFilters = { flaggedOnly, limit: 100 }
  const statusFilter = STATUS_FILTER[pivot]
  if (statusFilter) filters.status = statusFilter
  if (agentFilter.trim()) filters.agentUserId = agentFilter.trim()
  if (fromDate) filters.fromDate = fromDate
  if (toDate) filters.toDate = toDate
  const review = useTripReview(filters)
  // Cosmetic: the trip detail endpoint checks TRIP_DETAIL_VIEW and scope itself.
  const { data: me } = useMe()
  const canOpenTrip = navPermissionCheck(me)('TRIP_DETAIL_VIEW')

  const items = useMemo(() => review.data?.items ?? [], [review.data])
  const flaggedCount = useMemo(
    () => items.filter((i) => i.integrityFlagCount > 0).length,
    [items],
  )
  const totalDistanceKm = useMemo(() => {
    const sum = items.reduce(
      (acc, i) => acc + parseFloat(i.actualDistanceM ?? '0'),
      0,
    )
    return sum / 1000
  }, [items])

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ClipboardList className="h-5 w-5" />
            Visit review
          </h1>
          <p className="text-sm text-muted-foreground">
            Every visit plan your reportees have run, with the metrics and integrity
            flags from the T3 completion pass.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => review.refetch()}
          disabled={review.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${review.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label="Visit plans in view" value={items.length} />
        <Kpi
          label="Total distance"
          value={totalDistanceKm.toFixed(totalDistanceKm >= 10 ? 0 : 1)}
          hint="kilometres"
        />
        <Kpi
          label="Integrity-flagged"
          value={flaggedCount}
          hint={flaggedCount > 0 ? 'Click "Flagged only" to focus' : 'Clean'}
        />
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <ChipGroup>
              <Chip
                active={pivot === 'completed'}
                onClick={() => setPivot('completed')}
              >
                Completed
              </Chip>
              <Chip
                active={pivot === 'active'}
                onClick={() => setPivot('active')}
              >
                Active
              </Chip>
              <Chip active={pivot === 'all'} onClick={() => setPivot('all')}>
                All
              </Chip>
            </ChipGroup>
            <Chip
              active={flaggedOnly}
              onClick={() => setFlaggedOnly((f) => !f)}
            >
              <Flag className="mr-1 h-3 w-3" />
              Flagged only
            </Chip>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="agent-filter">Agent UUID</Label>
              <Input
                id="agent-filter"
                placeholder="Optional — narrow to one reportee"
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="from-date">From date</Label>
              <Input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="to-date">To date</Label>
              <Input
                id="to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          {review.isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load review list</AlertTitle>
              <AlertDescription>
                {isAxiosError(review.error)
                  ? review.error.message
                  : String(review.error)}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>

        <CardContent>
          {review.isLoading && <SkeletonRows rows={5} />}
          {!review.isLoading && items.length === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No visit plans match these filters yet.
            </p>
          )}
          <ul className="space-y-3">
            {items.map((item) => (
              <TripRow key={item.tripId} item={item} canOpen={canOpenTrip} />
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------

function TripRow({ item, canOpen }: { item: TripReviewItem; canOpen: boolean }) {
  const distance = formatDistance(item.actualDistanceM)
  const efficiency = formatEfficiency(item.travelEfficiency)
  const flagged = item.integrityFlagCount > 0
  const active = item.status === 'ACTIVE'

  const row = (
    <li
      className={`rounded-lg border bg-card p-4 shadow-sm transition-colors hover:bg-slate-50 ${
        flagged
          ? 'border-amber-300 bg-amber-50/40 hover:bg-amber-50/60'
          : active
            ? 'border-blue-300 bg-blue-50/30 hover:bg-blue-50/60'
            : 'border-slate-200'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <StatusBadge status={item.status} />
            {flagged && (
              <Badge tone="warning">
                <Flag className="mr-1 h-3 w-3" />
                {item.integrityFlagCount} flagged
              </Badge>
            )}
            {item.endReason && item.endReason !== 'USER_ENDED' && (
              <Badge tone="slate">{labelReason(item.endReason)}</Badge>
            )}
          </div>
          <div className="text-sm font-medium">
            Agent <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{shortId(item.agentUserId)}</code>
            {item.startedAt && (
              <>
                <span className="mx-2 text-slate-300">·</span>
                <CalendarDays className="mr-1 inline h-3 w-3" />
                {dateTime(item.startedAt)}
                <span className="ml-1 text-xs text-muted-foreground">
                  ({relativeTime(item.startedAt)})
                </span>
              </>
            )}
          </div>
          {(item.deviceModel || item.osVersion || item.appVersion) && (
            <div className="text-xs text-muted-foreground">
              <Smartphone className="mr-1 inline h-3 w-3" />
              {[item.deviceModel, item.osVersion, item.appVersion]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-4 text-right">
          <Metric icon={<Ruler className="h-3 w-3" />} label="Distance" value={distance} />
          <Metric icon={<Gauge className="h-3 w-3" />} label="Efficiency" value={efficiency} />
          {item.startedLat && item.startedLng && (
            <Metric
              icon={<MapPin className="h-3 w-3" />}
              label="Start"
              value={`${item.startedLat}, ${item.startedLng}`}
              monospace
            />
          )}
        </div>
      </div>
    </li>
  )
  // Opening a trip needs Visit details (TRIP_DETAIL_VIEW); without it the
  // row is a summary only rather than a link to a page that would 404.
  if (!canOpen) return row
  return (
    <Link
      to="/trips/$tripId"
      params={{ tripId: item.tripId }}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
    >
      {row}
    </Link>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone: 'positive' | 'info' | 'slate' =
    status === 'COMPLETED'
      ? 'positive'
      : status === 'ACTIVE'
        ? 'info'
        : 'slate'
  return <Badge tone={tone}>{status.replace(/_/g, ' ')}</Badge>
}

function Metric({
  icon,
  label,
  value,
  monospace = false,
}: {
  icon: React.ReactNode
  label: string
  value: string
  monospace?: boolean
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-end gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`text-sm font-semibold ${monospace ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// formatters
// ---------------------------------------------------------------------------

function formatDistance(metres: string | null): string {
  if (!metres) return '—'
  const n = parseFloat(metres)
  if (!Number.isFinite(n)) return '—'
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)} km`
  return `${Math.round(n)} m`
}

function formatEfficiency(eff: string | null): string {
  if (!eff) return '—'
  const n = parseFloat(eff)
  if (!Number.isFinite(n)) return '—'
  return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`
}

function labelReason(code: string): string {
  switch (code) {
    case 'USER_ENDED':
      return 'User ended'
    case 'FORCED_INTERRUPT':
      return 'Forced interrupt'
    case 'AUTO_NO_FIX':
      return 'Auto — no fix'
    case 'HARD_CAP_24H':
      return 'Auto — 24h cap'
    case 'FORCE_CLOSED':
      return 'Force-closed'
    default:
      return code.replace(/_/g, ' ').toLowerCase()
  }
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}
